import Link from '~/src/common/components/Link';
import EmptySmall from '~/src/common/components/States/Empty/Small';
import ErrorSmall from '~/src/common/components/States/Error/Small';
import LoadingSmall from '~/src/common/components/States/Loading/Small';
import Sticker from '~/src/common/components/Sticker';
import { useElementVisibility } from '~/src/common/hooks/useElementVisibility';
import I18n from '~/src/common/services/I18n';
import { type SubCategory } from '~/src/common/typings/category';
import { sanitize } from '~/src/common/utils/text';
import { useGetCategory } from '~/src/queries/api-ecom/generated/api-ecom';

import Products from '../Products/Products';
import { getCategoryPageLink, getSubcategoryAnchor } from '../utils';

import styles from './Subcategory.module.scss';

interface Props {
  subcategory: SubCategory;
}

const Subcategory = ({ subcategory }: Props) => {
  // Condition de chargement de la catégorie
  const [ref, visible] = useElementVisibility();
  const enabled = visible && subcategory.itemCount > 0;

  // Chargement de la catégorie et extraction des infos
  const query = useGetCategory(subcategory.slug, { query: { enabled } });
  const { data, refetch, isError } = query;

  //Les différents états possible de l'affichage
  const isEmpty = subcategory.itemCount === 0;
  const isLoaded = !isEmpty && !!data?.items.length;
  const isFailure = !isEmpty && !isLoaded && isError;
  const isWaiting = !isEmpty && !isLoaded && !isFailure;

  // Description affichée uniquement si non vide
  const description = sanitize(subcategory.description ?? '');

  return (
    <section
      className={styles.container}
      ref={ref}
      aria-hidden={isEmpty}
      data-observed={subcategory.id}
      id={getSubcategoryAnchor(subcategory)}>
      <header>
        <h2>
          <Link href={getCategoryPageLink(subcategory)}>{subcategory.title}</Link>
        </h2>
        {subcategory.label ? (
          <Sticker fg={subcategory.label.fg} bg={subcategory.label.bg}>
            {subcategory.label.text}
          </Sticker>
        ) : null}
      </header>

      {description ? (
        <summary>
          <p>{description}</p>
        </summary>
      ) : null}

      {isEmpty ? <EmptySmall text={I18n.t('category.empty.small.text')} withIcon /> : null}
      {isLoaded ? <Products category={data} /> : null}
      {isFailure ? (
        <ErrorSmall
          text={I18n.t('category.error.small.text')}
          button={I18n.t('category.error.small.btn')}
          onClickRetry={refetch}
        />
      ) : null}
      {isWaiting ? <LoadingSmall count={subcategory.itemCount} /> : null}
    </section>
  );
};

export default Subcategory;
